import { useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
  FormHelperText,
  Box,
  CircularProgress,
  Stack,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { isKeyInObject } from 'shared/types';
import {
  NotificationOption,
  NotificationSetting,
} from 'features/notifications/notifications.types';
import { useNotifications } from 'features/notifications/hooks/useNotifications';
import {
  NotificationSettingTypes,
  NotificationTypes,
} from 'features/notifications/notifications.constants';

type CountyNotificationDialogProps = {
  open: boolean;
  onClose: () => void;
  notificationSetting: NotificationSetting | null;
};

type NotificationSettingOption = Extract<
  NotificationOption,
  'all' | 'new_incidents_only'
>;

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: Number(theme.shape.borderRadius) * 1.34,
    padding: theme.spacing(3, 2),
    width: '100%',
    [theme.breakpoints.down(480)]: {
      margin: theme.spacing(2),
    },
  },
  title: {
    padding: 0,
  },
  content: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  checkbox: {
    padding: 4,
    marginLeft: 5,
    marginRight: 5,
  },
  helperText: {
    fontSize: theme.typography.subtitle1.fontSize,
  },
  actions: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  loaderContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

const notificationSettingToOptions = {
  [NotificationSettingTypes.all]: NotificationSettingTypes.all,
  [NotificationSettingTypes.allWithSilent]: NotificationSettingTypes.all,
  [NotificationSettingTypes.onlyNew]: NotificationSettingTypes.onlyNew,
  [NotificationSettingTypes.onlyNewWithSilent]:
    NotificationSettingTypes.onlyNew,
};

const getNotificationSettingOption = (
  setting: NotificationOption | undefined,
): (typeof notificationSettingToOptions)[keyof typeof notificationSettingToOptions] => {
  const key =
    setting && isKeyInObject(setting, notificationSettingToOptions)
      ? setting
      : NotificationSettingTypes.all;

  return notificationSettingToOptions[key];
};

export const CountyNotificationDialog = (
  props: CountyNotificationDialogProps,
): JSX.Element => {
  const { open, onClose, notificationSetting } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [includeSilent, setIncludeSilent] = useState(
    notificationSetting?.setting === NotificationSettingTypes.allWithSilent ||
      notificationSetting?.setting ===
        NotificationSettingTypes.onlyNewWithSilent,
  );
  const [notificationOption, setNotificationOption] =
    useState<NotificationSettingOption>(
      getNotificationSettingOption(notificationSetting?.setting),
    );
  const { putNotificationSettings } = useNotifications();

  const handleSave = (): void => {
    if (!notificationSetting) return;

    let setting: NotificationOption = notificationOption;

    if (includeSilent) {
      if (setting === NotificationSettingTypes.all) {
        setting = NotificationSettingTypes.allWithSilent;
      } else if (setting === NotificationSettingTypes.onlyNew) {
        setting = NotificationSettingTypes.onlyNewWithSilent;
      }
    }

    putNotificationSettings.mutate(
      [
        {
          ...notificationSetting,
          setting,
        },
      ],
      {
        onSuccess: () => onClose(),
      },
    );
  };

  const handleRemove = (): void => {
    if (!notificationSetting) return;
    putNotificationSettings.mutate(
      [
        {
          ...notificationSetting,
          setting: NotificationSettingTypes.off,
        },
      ],
      {
        onSuccess: () => onClose(),
      },
    );
  };

  return (
    <Dialog
      aria-labelledby="county-notification-dialog-title"
      open={open}
      onClose={onClose}
      classes={{ paper: classes.paper }}
    >
      <DialogTitle
        id="county-notification-dialog-title"
        className={classes.title}
      >
        <Typography variant="h3" component="span">
          {notificationSetting?.region.displayName}
        </Typography>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <Stack spacing={2} sx={{ marginTop: 2 }}>
          <TextField
            select
            id="field-control-updates"
            label={t('notifications.dialog.inputs.notificationOption.label')}
            fullWidth
            value={notificationOption}
            onChange={(e) =>
              setNotificationOption(e.target.value as NotificationSettingOption)
            }
            disabled={putNotificationSettings.isPending}
          >
            <MenuItem value={NotificationTypes.ALL.key}>
              {NotificationTypes.ALL.displayName}
            </MenuItem>
            <MenuItem value={NotificationTypes.NEW_INCIDENTS_ONLY.key}>
              {NotificationTypes.NEW_INCIDENTS_ONLY.displayName}
            </MenuItem>
          </TextField>
          <Box component="form" noValidate>
            <FormControlLabel
              control={
                <Checkbox
                  name="isPrescribed"
                  checked={includeSilent}
                  onChange={(event) => setIncludeSilent(event.target.checked)}
                  className={classes.checkbox}
                />
              }
              label={t('notifications.dialog.inputs.includeSilent.label')}
              disabled={putNotificationSettings.isPending}
            />
            <FormHelperText className={classes.helperText}>
              {t('notifications.dialog.inputs.includeSilent.helperText')}
            </FormHelperText>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Stack spacing={2} sx={{ width: '100%' }}>
          <Button
            fullWidth
            size="large"
            onClick={handleSave}
            disabled={putNotificationSettings.isPending}
          >
            {t('notifications.dialog.buttons.save')}
          </Button>
          <Button
            fullWidth
            size="large"
            onClick={handleRemove}
            disabled={putNotificationSettings.isPending}
            color="error"
          >
            {t('common.delete')}
          </Button>
          <Button
            fullWidth
            size="large"
            onClick={onClose}
            disabled={putNotificationSettings.isPending}
            color="neutral"
          >
            {t('notifications.dialog.buttons.cancel')}
          </Button>

          {putNotificationSettings.isPending && (
            <Box className={classes.loaderContainer}>
              <CircularProgress size={32} />
            </Box>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
